import { ApolloError } from '@apollo/client';

import { getErrorMessage } from 'src/utils/graphqlErrorHandler';

const SETTINGS_REQUIREMENT_PREFIX = 'settings.';

function getMissingRequirements(error: ApolloError): string[] {
  const missingRequirements = error.graphQLErrors?.[0]?.extensions?.missingRequirements;
  return Array.isArray(missingRequirements)
    ? missingRequirements.filter((value): value is string => typeof value === 'string')
    : [];
}

function getMissingRequirementMessage(missingRequirements: string[]): string | null {
  if (missingRequirements.includes('trackedLocations')) {
    return 'Select at least one tracked location to continue.';
  }

  if (missingRequirements.includes('trackedProducts')) {
    return 'Track at least one product to continue.';
  }

  if (
    missingRequirements.includes('defaultWarehouse') ||
    missingRequirements.some((requirement) => requirement.startsWith(SETTINGS_REQUIREMENT_PREFIX))
  ) {
    return 'Finish the required settings before continuing.';
  }

  return null;
}

export function getOnboardingErrorMessage(
  error: unknown,
  fallbackMessage = 'Failed to update onboarding.'
): string {
  if (error instanceof ApolloError) {
    const errorCode = error.graphQLErrors?.[0]?.extensions?.code;
    const missingRequirementMessage = getMissingRequirementMessage(getMissingRequirements(error));

    if (typeof errorCode === 'string') {
      if (errorCode === 'SUBSCRIPTION_REQUIRED') {
        return 'Activate billing to continue.';
      }

      if (errorCode === 'ONBOARDING_STEP_PREREQUISITE_FAILED') {
        return 'Complete the earlier onboarding steps first.';
      }

      if (
        (errorCode === 'ONBOARDING_STEP_REQUIREMENTS_FAILED' ||
          errorCode === 'ONBOARDING_INCOMPLETE') &&
        missingRequirementMessage
      ) {
        return missingRequirementMessage;
      }
    }
  }

  return getErrorMessage(error) || fallbackMessage;
}
